import { type SWRInfiniteConfiguration } from 'swr/infinite';
import {
  type PaginationQuery,
  AccountTransaction,
} from '@mls-wallet/shared-types';
import { type PaginatedResponse, usePagination } from '@/hooks/use-pagination';

type QueuedTransactionsResponse = PaginatedResponse<AccountTransaction>;

/**
 * Queue-specific wrapper around the generic pagination hook.
 *
 * Notes:
 * - `refreshInterval` and the rest of the SWR options are passed through so
 *   existing polling call sites, like the pending-signatures modal, can keep
 *   using this hook as their polling source.
 * - `enabled` is used by the transactions page so only the active tab fetches.
 */
type UseQueuedTransactionsOptions = {
  refreshInterval?: SWRInfiniteConfiguration<QueuedTransactionsResponse>['refreshInterval'];
  pagination?: PaginationQuery;
  enabled?: boolean;
} & SWRInfiniteConfiguration<QueuedTransactionsResponse>;

/**
 * Fetch queued transactions from the session-auth endpoint.
 *
 * The backend route applies queued-specific visibility rules, including hiding
 * transactions that should not render in ui-web.
 */
export function useQueuedTransactions(options?: UseQueuedTransactionsOptions) {
  const { pagination, enabled, ...swr } = options ?? {};

  const result = usePagination<AccountTransaction>({
    // The queued route applies the server-side queued-only visibility rules,
    // including hiding rows that the web UI should not render.
    path: 'transactions/queued',
    ...(pagination ? { pagination } : {}),
    ...(enabled !== undefined ? { enabled } : {}),
    ...(Object.keys(swr).length > 0
      ? {
          swr: swr as SWRInfiniteConfiguration<QueuedTransactionsResponse>,
        }
      : {}),
  });

  return {
    transactions: result.data,
    pagination: result.pagination,
    hasMore: result.hasMore,
    isLoading: result.isLoading,
    isLoadingMore: result.isLoadingMore,
    error: result.error,
    loadMore: result.loadMore,
    refetch: result.refetch,
    reset: result.reset,
  };
}
